import { searchLocationWithTransaction, searchStaffWithTransaction } from "apis";
import {
  Field,
  Filter,
  MultipleSelect,
  RangePicker,
  Select,
  Text,
  TimePicker,
} from "components/commons";
import { TapStatus, VoucherType } from "enums";
import { useApi, useMount } from "hooks";
import {
  locationWithTransactionFilterResponse,
  staffWithTransactionFilterRequest,
  staffWithTransactionFilterResponse,
  locationMovementFilterRequest,
} from "mappers";
import React, { useCallback, useMemo } from "react";
import { mapObject, mapObjectsToSelect, prettifyTapStatus, prettifyVoucherType } from "services";
import lang from "translations";
import { modifyFromTimeRange, modifyToTimeRange } from "services";

const VoucherMovementFilter = ({
  filterState,
  requestState,
  modifyFilter,
  applyFilter,
  clearFilter,
  venueId,
}) => {
  const {
    request: searchLocationRequest,
    loading: loadingLocation,
    mappedData: locations,
  } = useApi({
    api: searchLocationWithTransaction,
    isArray: true,
    mapper: locationWithTransactionFilterResponse,
    handleOwnError: true,
    params: {
      venueId,
    },
  });

  const {
    request: searchStaffRequest,
    loading: loadingStaff,
    mappedData: staffs,
  } = useApi({
    api: searchStaffWithTransaction,
    isArray: true,
    mapper: staffWithTransactionFilterResponse,
    handleOwnError: true,
    params: {
      venueId,
    },
  });

  useMount(() => {
    fetchLocationRequest(requestState);
    fetchStaffRequest(requestState);
  });

  const fetchLocationRequest = useCallback(
    (requestState) => {
      searchLocationRequest(mapObject({ ...requestState }, locationMovementFilterRequest));
    },
    [searchLocationRequest]
  );

  const fetchStaffRequest = useCallback(
    (requestState) => {
      searchStaffRequest(mapObject({ ...requestState }, staffWithTransactionFilterRequest));
    },
    [searchStaffRequest]
  );

  const applyFilterCb = useCallback(
    (searchKey) => {
      applyFilter(searchKey);
    },
    [applyFilter]
  );

  const clearFilterCb = useCallback(async () => {
    const { requestState } = clearFilter();
    fetchLocationRequest(requestState);
    fetchStaffRequest(requestState);
  }, [clearFilter, fetchLocationRequest, fetchStaffRequest]);

  const changeDateRangeCb = useCallback(
    (name, value) => {
      const { requestState } = modifyFilter(name, { value });
      fetchLocationRequest(requestState);
      fetchStaffRequest(requestState);
    },
    [fetchLocationRequest, fetchStaffRequest, modifyFilter]
  );

  const locationOptions = useMemo(() => {
    return mapObjectsToSelect(locations, { textKey: "name" });
  }, [locations]);

  const staffOptions = useMemo(() => {
    return mapObjectsToSelect(staffs, { textKey: "name" });
  }, [staffs]);

  const voucherTypeOptions = useMemo(() => {
    const voucherTypes = [VoucherType.Issue, VoucherType.Redeem, VoucherType.Remove];
    return voucherTypes.map((type) => {
      return { text: prettifyVoucherType(type), value: type };
    });
  }, []);

  const tapStatusOptions = useMemo(() => {
    const tapStatus = [TapStatus.Success, TapStatus.Failed];
    const prettyTapStatus = tapStatus.map((status) => {
      return { text: prettifyTapStatus(status), value: status };
    });
    return [{ text: lang.allStatus, value: null }, ...prettyTapStatus];
  }, []);

  return (
    <Filter
      placeholder={lang.searchMovementPlaceholder}
      className="mb-lg"
      onClear={clearFilterCb}
      onApply={applyFilterCb}
      filterState={filterState}
      actionsSpan={12}
    >
      <Field className="col-span-4" filterLabel={lang.dateRange}>
        <RangePicker name="dateRange" onChange={changeDateRangeCb} value={filterState.dateRange} />
      </Field>
      <Field className="col-span-4" filterLabel={lang.timeRange}>
        <div className="flex items-center">
          <TimePicker
            value={filterState.dateRange[0]}
            onChange={(val) => {
              modifyFromTimeRange(val, filterState, modifyFilter);
            }}
          />
          <Text className="mx-sm" color="text-black-light">
            {lang.to}
          </Text>
          <TimePicker
            value={filterState.dateRange[1]}
            onChange={(val) => {
              modifyToTimeRange(val, filterState, modifyFilter);
            }}
          />
        </div>
      </Field>
      <Field className="col-span-4" filterLabel={lang.outlet}>
        <MultipleSelect
          name="locations"
          selectAllText={lang.allLocations}
          loading={loadingLocation}
          options={locationOptions}
          value={filterState.locations}
          onChange={(name, obj) => {
            modifyFilter(name, { value: obj.value });
          }}
          emptyPlaceHolder={lang.noLocationAvailable}
          placeholder={lang.selectLocation}
        />
      </Field>
      <Field className="col-span-4" filterLabel={lang.staff}>
        <MultipleSelect
          name="staffs"
          selectAllText={lang.allStaff}
          loading={loadingStaff}
          options={staffOptions}
          value={filterState.staffs}
          onChange={(name, obj) => {
            modifyFilter(name, { value: obj.value });
          }}
          emptyPlaceHolder={lang.noAvailableStaff}
          placeholder={lang.selectStaff}
        />
      </Field>
      <Field className="col-span-4" filterLabel={lang.voucherType}>
        <MultipleSelect
          name="voucherTypes"
          selectAllText={lang.allVoucherTypes}
          options={voucherTypeOptions}
          value={filterState.voucherTypes}
          onChange={(name, obj) => {
            modifyFilter(name, { value: obj.value });
          }}
        />
      </Field>
      <Field className="col-span-4" filterLabel={lang.tapStatus}>
        <Select
          name="tapStatus"
          options={tapStatusOptions}
          value={filterState.tapStatus}
          onChange={modifyFilter}
        />
      </Field>
    </Filter>
  );
};

export default VoucherMovementFilter;
